<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', $event)" size="medium" :zIndex="1200" isWithScroll :isClosable="false">
    <template #header>
      <div class="location-editor__header">
        <h2 class="global-h2">{{ props.location ? "Edit" : "Add" }} {{ editorKind }}</h2>
        <p>Search for a location to auto-fill its social accounts, or manually enter.</p>
      </div>
    </template>
    <template #content>
      <LocationEditorForm ref="formRef" :customerId="props.customerId" :location="props.location" @saved="handleSaved" />
    </template>
    <template #footer="{ closeModal }">
      <div class="location-editor__footer">
        <div class="location-editor__archive-area">
          <template v-if="props.location">
            <SrpButton v-if="props.location.deletedDate" tag="button" color="gray" fill="outlined" size="small" :isDisabled="isChangingArchive || isSaving" @click.stop="changeArchive(false)">Unarchive</SrpButton>
            <LinkWithIcon v-else tag="button" class="location-editor__archive-link" isDottedUnderline :isDisabled="isChangingArchive || isSaving" @click.stop="changeArchive(true)">
              <template #icon><IconEmbedded name="trashcan_2" :size="16" /></template>
              Archive
            </LinkWithIcon>
          </template>
          <span v-if="archiveError" class="location-editor__archive-error">{{ archiveError }}</span>
        </div>
        <div class="location-editor__footer-actions">
          <SrpButton color="gray" fill="outlined" :isDisabled="isSaving || isChangingArchive" @click.stop="closeModal()">Cancel</SrpButton>
          <SrpButton :isDisabled="!canSave || isChangingArchive" @click.stop="formRef?.save()">
            <template #icon><LoadingIcon v-if="isSaving" :size="18" /><IconEmbedded v-else name="save_2-5" :size="18" /></template>
            Save
          </SrpButton>
        </div>
      </div>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationEditorForm from "./LocationEditorForm.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import type { LocationData } from "@contracts/socialLocations";

const props = withDefaults(defineProps<{ isVisible: boolean; customerId: string; location?: LocationData | null }>(), { location: null });
const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "saved", location: LocationData): void;
}>();

// The fields and all of their logic live in LocationEditorForm, shared with the "New Album" modal.
const formRef = ref<InstanceType<typeof LocationEditorForm> | null>(null);
const isSaving = computed(() => Boolean(formRef.value?.isSaving));
const canSave = computed(() => Boolean(formRef.value?.canSave) && !isSaving.value && !formRef.value?.isBusy);
const editorKind = computed(() => props.location?.tagKind ?? formRef.value?.tagKind ?? "Location");
const isChangingArchive = ref(false);
const archiveError = ref("");
watch([() => props.location, () => props.isVisible], () => (archiveError.value = ""));

async function changeArchive(archive: boolean) {
  if (!props.location || isChangingArchive.value || isSaving.value) return;
  isChangingArchive.value = true;
  archiveError.value = "";
  try {
    if (archive) await SocialLocationsRepo.archive(props.customerId, props.location.id);
    else await SocialLocationsRepo.unarchive(props.customerId, props.location.id);
    emit("update:isVisible", false);
    emit("saved", { ...props.location, deletedDate: archive ? new Date().toISOString() : null });
  } catch {
    archiveError.value = archive ? "Could not archive this account. Please try again." : "Could not unarchive this account. Please try again.";
  } finally {
    isChangingArchive.value = false;
  }
}

function handleSaved(location: LocationData) {
  emit("update:isVisible", false);
  emit("saved", location);
}
</script>

<style scoped lang="scss">
.location-editor__header {
  display: grid;
  gap: 4px;
}
.location-editor__header h2,
.location-editor__header p {
  margin: 0;
}
.location-editor__header p {
  // color: #667085;
  font-size: 15px;
}
.location-editor__footer {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.location-editor__archive-area {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.location-editor__archive-link {
  padding: 0;
  border: 0;
  background: transparent;
}
.location-editor__archive-error {
  color: #b42318;
  font-size: 13px;
}
.location-editor__footer-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
  justify-content: flex-end;
  gap: 12px;
}
</style>
